import { getConfig } from './config'

export default (ctx) => {
    const {
        app,
        store,
        $config,
        $sitewideData,
        $axios,
        $integrationsManager,
        $notifications,
        $uiEvents,
    } = ctx

    const config = getConfig($config.platform.mode)

    const loader = new $integrationsManager.loaders.OnDemandLoader(
        [{ src: 'https://sdk.helloextend.com/extend-sdk-client/v1/extend-sdk-client.min.js' }],
        false,
        () => window.Extend.config(config)
    )

    // Simple state to track if the modal is open
    let isModalOpen = false

    async function openModal(item) {
        // If modal is already open then we shouldn't do anything
        if (isModalOpen) return
        isModalOpen = true

        try {
            // Make sure extend is loaded
            await _load()

            // Add guard so we can close the modal if the route changes
            const removeCloseGuard = app.router.beforeEach((to, from, next) => {
                window.Extend.modal.close()

                isModalOpen = false

                removeCloseGuard()

                next()
            })

            // We need to manually get the plans offered because extend doesn't tell us about
            // the offers they showed to the user in the modal.
            // We are starting this promise here so it can run in the background. We will only use its info
            // when the modal closes
            const plansRequest = getPlans(item.sku)

            const extendModalPromise = new Promise((resolve) => {
                window.Extend.modal.open({
                    referenceId: item.sku,
                    // This function will run when the extend modal closes
                    // when the user accept/decline protection, clicks off the modal, or extend has nothing to offer
                    async onClose(plan, product) {
                        // If this function runs then that means the modal closed so we can remove our
                        // route guard which would close the modal if the user hit the back/forward button
                        // and reset our open bool
                        removeCloseGuard()
                        isModalOpen = false

                        // Make sure the plans that we requested finished
                        const plans = await plansRequest

                        // If we have plans then we need to make sure we report that
                        if (plans) {
                            $uiEvents.$emit('warrantyOfferMade', {
                                ...item,
                                warranty: plans,
                            })
                        }

                        // When both of these exist then the user selected a plan and we need to add it to the cart
                        // If they do not exist then extend didn't have any plans to offer the user
                        if (plan && product) {
                            try {
                                // For some reason, extend formats this plan obj differently than the getOffers endpoint.
                                // So we need to make sure an id prop exists in the plan so the whole system can use the same id prop
                                const formattedPlan = { id: plan.planId, ...plan }

                                await addWarranty(item, formattedPlan)
                            } catch (error) {
                                $notifications.addError('Unable to add warranty')
                                app.$log.error('EXTEND_ERROR: onClose', error)
                            }
                        } else if (plans) {
                            // If we make it here then the user either declined protection/closed the modal
                            // But if extend did have plans for the item then we need to trigger the decline event
                            $uiEvents.$emit('warrantyDeclined', {
                                ...item,
                                warranty: plans,
                            })
                        }

                        resolve()
                    },
                })
            })

            // This will wait for the extend action to complete before we resolve this function
            await extendModalPromise
        } catch (error) {
            app.$log.error('EXTEND_ERROR: openModal', error)
        }
    }

    async function addWarranty(cartItem, plan) {
        // If we don't have a cartItem or plan then don't do this
        if (!cartItem || !plan) return

        try {
            await $axios.post(
                `${$config.platform.apis.cart}${store.state.permId}/items/${cartItem.itemId}/warranty`,
                {
                    plan: plan.id,
                    qty: cartItem.qty,
                },
                {
                    headers: {
                        'x-site': $sitewideData.sitePrefix,
                    },
                }
            )

            $uiEvents.$emit('addToCart', {
                productName: 'EXTEND-WARRANTY',
                sku: plan.id,
                qty: cartItem.qty,
                salePrice: plan.price,
            })
            $uiEvents.$emit('addWarranty', {
                ...cartItem,
                warranty: plan,
            })

            await store.dispatch('cart/getCart')
        } catch (err) {
            throw err.response.data
        }
    }

    async function removeWarranty(cartItem) {
        // If we don't have a warranty then we shouldn't do this
        if (!cartItem.warranty) return

        try {
            await $axios.delete(
                `${$config.platform.apis.cart}${store.state.permId}/items/${cartItem.itemId}/warranty`,
                {
                    headers: {
                        'x-site': $sitewideData.sitePrefix,
                    },
                }
            )

            $uiEvents.$emit('removeFromCart', {
                productName: 'EXTEND-WARRANTY',
                sku: cartItem.warranty.id,
                qty: cartItem.qty,
                salePrice: cartItem.warranty.price,
            })
            $uiEvents.$emit('removeWarranty', {
                ...cartItem,
                // Just doing this to make sure that we don't carry any reactivity into this on accident
                warranty: { ...cartItem.warranty },
            })

            await store.dispatch('cart/getCart')
        } catch (err) {
            throw err.response.data
        }
    }

    async function getPlans(sku) {
        let plans

        try {
            await _load()

            if (sku) {
                const extendOffersResponse = await window.Extend.getOffer(sku)

                // Example: Plans Payload
                // {
                //     "adh": [],
                //     "base": [],
                //     "roadhazard": [],
                //     "recommended": "base" // this is the value of the key offered: adh, base, roadhazard
                //   }

                // Check that the resp is in the format that we are expecting
                if (extendOffersResponse.plans?.recommended) {
                    // Get the plans that extend offers for the sku
                    const plansToOffer =
                        extendOffersResponse.plans[extendOffersResponse.plans.recommended] || []

                    // If we have plans then we need to set them to the return var
                    if (plansToOffer.length > 0)
                        plans = extendOffersResponse.plans[extendOffersResponse.plans.recommended]
                }
            }
        } catch (error) {
            app.$log.error('EXTEND_ERROR: getOffer', error)
        }

        return plans
    }

    function _load() {
        return new Promise((resolve, reject) => {
            loader.load(
                () => resolve(),
                () => reject(new Error('Unable to load Extend'))
            )
        })
    }

    return {
        openModal,
        getPlans,
        addWarranty,
        removeWarranty,
    }
}
